import { cn } from '@/lib/utils';
import { DatePicker } from './DatePicker';
import { toIsoDate } from './CalendarDatePicker';
import type { DateRange } from '@/types/common.types';

export interface DateRangePickerProps {
  label?: string;
  error?: string;
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
  startLabel?: string;
  endLabel?: string;
}

/**
 * Local calendar date, not `toISOString()`: that returns the UTC date, so the local
 * midnight `fromIso` produces read back as the previous day anywhere west of UTC.
 */
function toIso(d: Date | null): string {
  if (!d || isNaN(d.getTime())) return '';
  return toIsoDate(d.getFullYear(), d.getMonth(), d.getDate());
}

function fromIso(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime()) ? null : d;
}

export function DateRangePicker({
  label,
  error,
  value,
  onChange,
  disabled,
  className,
  wrapperClassName,
  startLabel = 'Start date',
  endLabel = 'End date',
}: DateRangePickerProps) {
  const startIso = toIso(value?.startDate ?? null);
  const endIso = toIso(value?.endDate ?? null);

  const handleStartChange = (iso: string) => {
    onChange?.({
      startDate: fromIso(iso),
      endDate: value?.endDate ?? null,
    });
  };

  const handleEndChange = (iso: string) => {
    onChange?.({
      startDate: value?.startDate ?? null,
      endDate: fromIso(iso),
    });
  };

  return (
    <div className={cn('w-full', wrapperClassName)}>
      {label && (
        <p className="block text-sm font-medium text-slate-700 mb-1.5">{label}</p>
      )}

      <div className={cn('flex gap-3', className)}>
        <DatePicker
          label={startLabel}
          value={startIso}
          onChange={handleStartChange}
          max={endIso || undefined}
          disabled={disabled}
          wrapperClassName="flex-1"
        />
        <DatePicker
          label={endLabel}
          value={endIso}
          onChange={handleEndChange}
          min={startIso || undefined}
          disabled={disabled}
          wrapperClassName="flex-1"
        />
      </div>

      {error && (
        <p role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
